import React from 'react';
import { Link } from 'react-router-dom';
import type { League } from '@sulis-liiga/common';
import { LeagueStatus } from '@sulis-liiga/common';

interface LeagueCardProps {
  league: League;
}

const LeagueCard: React.FC<LeagueCardProps> = ({ league }) => {
  const getStatusColor = (status: LeagueStatus) => {
    switch (status) {
      case LeagueStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case LeagueStatus.INACTIVE:
        return 'bg-gray-100 text-gray-800';
      case LeagueStatus.PLANNING:
        return 'bg-blue-100 text-blue-800';
      case LeagueStatus.COMPLETED:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Link to={`/leagues/${league.id}`}>
      <div className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 p-6 h-full">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-lg font-semibold text-gray-900">{league.name}</h3>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(
              league.status,
            )}`}
          >
            {league.status}
          </span>
        </div>

        {league.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{league.description}</p>
        )}

        <div className="space-y-2">
          <div className="flex items-center text-sm text-gray-500">
            <svg
              className="h-4 w-4 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span>
              {league.location.city}, {league.location.country}
            </span>
          </div>

          <div className="flex items-center text-sm text-gray-500">
            <svg
              className="h-4 w-4 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <span>
              {league.administrators?.length ?? 0} administrator
              {(league.administrators?.length ?? 0) !== 1 ? 's' : ''}
            </span>
          </div>

          {league.registrationOpen && (
            <div className="mt-3">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                Registration Open
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default LeagueCard;
